// Support system validation utilities

import { z } from 'zod';
import type { SupportCategory, SupportPriority } from '@/types/support';
import { SUPPORT_FILE_CONFIG, SUPPORT_PRIORITY_COLORS } from '@/constants/support';

// Form data interface for QuickSupportModal
export interface SupportFormData {
  category: SupportCategory;
  priority: SupportPriority;
  subject: string;
  description: string;
  screenshots?: File[];
}

// Support request validation schema
export const supportRequestSchema = z.object({
  category: z.enum(['student_management', 'applications', 'capacity', 'transportation', 'reports', 'notifications', 'portal_sync', 'user_access', 'other']),
  priority: z.enum(['urgent', 'high', 'medium', 'low']),
  subject: z.string()
    .min(5, 'Subject must be at least 5 characters')
    .max(200, 'Subject must be less than 200 characters'),
  description: z.string()
    .min(20, 'Please provide more details (at least 20 characters)')
    .max(5000, 'Description must be less than 5000 characters'),
  screenshots: z.array(z.string().url()).optional(),
  metadata: z.record(z.any()).optional()
});

// Comment validation schema
export const supportCommentSchema = z.object({
  content: z.string()
    .min(1, 'Comment cannot be empty')
    .max(2000, 'Comment must be less than 2000 characters'),
  isInternal: z.boolean().optional()
});

// File validation
export function validateSupportFile(file: File): { valid: boolean; error?: string } {
  // Check file size
  if (file.size > SUPPORT_FILE_CONFIG.maxFileSize) {
    return {
      valid: false,
      error: `File size must be less than ${SUPPORT_FILE_CONFIG.maxFileSize / 1024 / 1024}MB`
    };
  }
  
  // Check file type
  if (!SUPPORT_FILE_CONFIG.acceptedFileTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Invalid file type. Accepted types: PNG, JPG, GIF, PDF'
    };
  }
  
  return { valid: true };
}

// Validate multiple files
export function validateSupportFiles(files: File[]): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Check total number of files
  if (files.length > SUPPORT_FILE_CONFIG.maxFiles) {
    errors.push(`Maximum ${SUPPORT_FILE_CONFIG.maxFiles} files allowed`);
  }
  
  // Validate each file
  files.forEach((file, index) => {
    const result = validateSupportFile(file);
    if (!result.valid && result.error) {
      errors.push(`File ${index + 1}: ${result.error}`);
    }
  });
  
  return {
    valid: errors.length === 0,
    errors
  };
}

// Sanitize user input
export function sanitizeSupportInput(input: string): string {
  // Remove any potential XSS attempts
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .trim();
}

// Validate priority based on category
export function validatePriorityForCategory(
  category: SupportCategory,
  priority: SupportPriority
): { valid: boolean; suggestedPriority?: SupportPriority } {
  // Technical issues should generally be high priority or above
  if (category === 'technical' && (priority === 'low' || priority === 'medium')) {
    return {
      valid: true, // Allow but suggest
      suggestedPriority: 'high'
    };
  }
  
  // Enrollment issues during enrollment period should be high priority
  const enrollmentPeriod = isEnrollmentPeriod();
  if (category === 'enrollment' && enrollmentPeriod && priority === 'low') {
    return {
      valid: true,
      suggestedPriority: 'medium'
    };
  }
  
  return { valid: true };
}

// Check if we're in enrollment period (example implementation)
function isEnrollmentPeriod(): boolean {
  const now = new Date();
  const month = now.getMonth(); // 0-11
  
  // Assume enrollment period is January-March (0-2)
  return month >= 0 && month <= 2;
}

// Validate request update
export const supportRequestUpdateSchema = z.object({
  status: z.enum(['open', 'in_progress', 'waiting_response', 'resolved', 'closed']).optional(),
  priority: z.enum(['urgent', 'high', 'medium', 'low']).optional(),
  assignedTo: z.string().uuid().optional(),
  metadata: z.record(z.any()).optional()
});

// Check if user can perform action
export function canUserUpdateRequest(
  userRole: string,
  requestStatus: string,
  action: 'close' | 'reopen' | 'assign' | 'change_priority'
): boolean {
  // Admin can do anything
  if (userRole === 'admin') return true;
  
  // School staff permissions
  if (userRole === 'school_staff') {
    switch (action) {
      case 'close':
        return requestStatus !== 'closed';
      case 'reopen':
        return requestStatus === 'closed' || requestStatus === 'resolved';
      case 'change_priority':
        return requestStatus !== 'closed';
      case 'assign':
        return false; // Only admin can assign
      default:
        return false;
    }
  }
  
  return false;
}

// Form error helper
export function getFormErrorMessage(error: any, field: string): string | undefined {
  if (!error) return undefined;
  
  // Zod validation error
  if (error.errors && Array.isArray(error.errors)) {
    const fieldError = error.errors.find((e: any) => e.path?.includes(field));
    return fieldError?.message;
  }
  
  // Direct field error
  if (error[field]) {
    return error[field];
  }
  
  return undefined;
}

// Get priority color class
export function getPriorityColor(priority: SupportPriority): string {
  return SUPPORT_PRIORITY_COLORS[priority] || 'bg-gray-100 text-gray-800';
}

// Format file size for display
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Validate support form data
export function validateSupportForm(data: SupportFormData & { screenshots?: File[] }): Array<{ field: string; message: string }> {
  const errors: Array<{ field: string; message: string }> = [];
  
  // Validate required fields
  if (!data.category) {
    errors.push({ field: 'category', message: 'Please select a category' });
  }
  
  if (!data.priority) {
    errors.push({ field: 'priority', message: 'Please select a priority' });
  }
  
  if (!data.subject || data.subject.trim().length < 5) {
    errors.push({ field: 'subject', message: 'Subject must be at least 5 characters' });
  }
  
  if (!data.description || data.description.trim().length < 20) {
    errors.push({ field: 'description', message: 'Please provide more details (at least 20 characters)' });
  }
  
  // Validate screenshots if provided
  if (data.screenshots && data.screenshots.length > 0) {
    const fileValidation = validateSupportFiles(data.screenshots);
    if (!fileValidation.valid) {
      fileValidation.errors.forEach(error => {
        errors.push({ field: 'screenshots', message: error });
      });
    }
  }
  
  return errors;
}